import type { EChartsOption, GridComponentOption } from '../echarts-types.ts';
import type { ChartTheme } from '../theme.ts';

/** Always transparent so the parent card background shows through (avoids ECharts default navy). */
export function baseOption(): Pick<EChartsOption, 'backgroundColor'> {
  return { backgroundColor: 'transparent' };
}

export function chartTitle(
  title: string | undefined,
  textColor: string,
  left: number | string = 4
): EChartsOption['title'] {
  if (!title) return undefined;
  return {
    text: title,
    textStyle: { fontSize: 11, fontWeight: 'bold', color: textColor },
    top: 4,
    left,
  };
}

export function multiSeriesLegend(
  seriesCount: number,
  textColor: string
): EChartsOption['legend'] {
  if (seriesCount <= 1) return undefined;
  return {
    bottom: 0,
    textStyle: { color: textColor, fontSize: 10 },
    itemWidth: 10,
    itemHeight: 10,
  };
}

/** Single tooltip option (ECharts also allows an array; we never use that). */
type TooltipOpts = Exclude<NonNullable<EChartsOption['tooltip']>, unknown[]>;

/** Keep tooltips off the chart host so hover cannot resize/remount the series. */
export function bodyTooltip(overrides: TooltipOpts = {}): TooltipOpts {
  return {
    appendTo: () => document.body,
    confine: false,
    enterable: false,
    ...overrides,
  };
}

export function cartesianGrid(
  title: string | undefined,
  seriesCount: number,
  overrides: Partial<GridComponentOption> = {}
): GridComponentOption {
  return {
    left: 8,
    right: 8,
    top: title ? 36 : 8,
    bottom: seriesCount > 1 ? 28 : 8,
    containLabel: true,
    ...overrides,
  };
}

export type OptionContext = {
  theme: ChartTheme;
  base: Pick<EChartsOption, 'backgroundColor'>;
};

export function optionContext(theme: ChartTheme): OptionContext {
  return { theme, base: baseOption() };
}
